import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-difference-modal>` says about itself.
 *
 * The comparison engine is not imported. It is looked up through `diff-engine.js`, so the library
 * ships with a working line comparison and an application that wants a word-level or syntax-aware
 * one installs it without this component or any page changing.
 *
 * Three cases it gets right that the obvious version does not. An item on only ONE side gets one
 * column and a sentence, not an empty second column — an empty column says "the other side has this,
 * and it is blank", which is a different and wronger fact. Two identical versions SAY they are
 * identical rather than being drawn as two columns with nothing highlighted, which leaves somebody
 * hunting for a change that is not there. And the counts are tallied from the rows the engine
 * actually produced, so the line at the top cannot disagree with the lines under it whatever engine
 * is installed.
 */
const docs: ComponentDocs = {
  aliases: ['diff view', 'side by side', 'difference viewer', 'compare detail', 'text diff'],

  answers: [
    'see exactly how two versions of something differ',
    'compare an item that exists in only one environment',
    'confirm two versions are identical',
    'read a difference without leaving the comparison',
  ],

  placements: ['appPage', 'recordPage'],

  whenToUse: 'When one item has been chosen out of a comparison and the question is HOW it differs.',

  insteadUse: [
    { name: 'CompareView', when: 'the question is WHICH items differ rather than how one of them does.' },
    { name: 'CodeEditor', when: 'the text is being edited rather than compared.' },
  ],

  inputs: [
    {
      name: 'item',
      attribute: 'item',
      type: 'text',
      description: 'What is being compared — shown as the heading and named in the "not in …" sentence.',
    },
    {
      name: 'sourceText',
      type: 'text',
      description: 'The source side’s text.',
    },
    {
      name: 'targetText',
      type: 'text',
      description: 'The target side’s text.',
    },
    {
      name: 'sourceName',
      attribute: 'source-name',
      type: 'text',
      description: 'What to call the source side.',
      default: 'Source',
    },
    {
      name: 'targetName',
      attribute: 'target-name',
      type: 'text',
      description: 'What to call the target side.',
      default: 'Target',
    },
    {
      name: 'missing',
      attribute: 'missing',
      type: 'text',
      options: ['source', 'target'],
      description:
        'The side the item is NOT on. One column is then drawn, and the other is replaced by a ' +
        'sentence — an empty column would claim the other side has a blank version of it.',
      fallback: 'both sides, when the attribute is absent or is neither word',
    },
    {
      name: 'closable',
      attribute: 'closable',
      type: 'boolean',
      description: 'Show a close control. Off by default, for a difference shown in place rather than over something.',
      default: false,
    },
  ],

  events: [
    { name: 'caos-difference-close', when: 'The close control is pressed.', carries: 'nothing' },
  ],

  state: [
    { name: 'rows', type: 'DiffRow[]', description: 'The comparison as the installed engine produced it. Empty for a one-sided item.' },
    {
      name: 'tally',
      type: '{ added, removed, equal }',
      description: 'Computed FROM the rows, so the line at the top cannot disagree with the lines under it.',
    },
    {
      name: 'identical',
      type: 'boolean',
      description: 'Whether the two sides match. A one-sided item is not identical — it is one-sided.',
    },
    { name: 'engine', type: 'string', description: 'The name of the installed comparison engine.' },
  ],

  parts: [
    { part: 'difference', is: 'The outer column.' },
    { part: 'header', is: 'The heading row.' },
    { part: 'title', is: 'The item’s name.' },
    { part: 'tally', is: 'The added/removed line, or the "only in …" / "identical" statement.' },
    { part: 'close', is: 'The close control.', when: '`closable` is set.' },
    { part: 'sides', is: 'The two-column area.' },
    { part: 'side', is: 'One side.' },
    { part: 'side-name', is: 'A side’s heading.' },
    { part: 'rows', is: 'A side’s scrolling lines.', when: 'That side has content.' },
    { part: 'row', is: 'One line.' },
    { part: 'gutter', is: 'A line’s number.' },
    { part: 'text', is: 'A line’s text.' },
    { part: 'absent', is: 'The "not in …" sentence.', when: 'The item is on only one side.' },
  ],

  accessibility:
    'Added and removed lines carry an `aria-label` naming what happened to them, so the change is ' +
    'spoken rather than conveyed only by the tint. The spacer rows that keep the two columns in step ' +
    'are `aria-hidden`, so a screen reader is not read a column of blanks. The tally is a ' +
    '`role="status"` live region, and for the two cases with no rows to read it states the answer in ' +
    'words — "Only in Target", "The two versions are identical" — rather than leaving somebody to ' +
    'infer it from an absence.',

  examples: [
    {
      key: 'differs',
      label: 'Two versions that differ',
      summary:
        'Side by side, with spacer rows keeping the columns in step — without them the sides drift ' +
        'apart by one line per change and after four changes nothing lines up.',
      placement: {
        component: 'caos-difference-modal',
        inputs: {
          item: 'BillingService',
          'source-name': 'UAT',
          'target-name': 'Production',
          sourceText:
            'public class BillingService {\n  private static final Integer SCALE = 2;\n  public static Decimal round(Decimal v) {\n    return v.setScale(SCALE);\n  }\n}',
          targetText:
            'public class BillingService {\n  private static final Integer SCALE = 4;\n  public static Decimal round(Decimal v) {\n    if (v == null) return null;\n    return v.setScale(SCALE);\n  }\n}',
        },
      },
      stage: { needsWidth: 860 },
    },
    {
      key: 'one-sided',
      label: 'An item on only one side',
      summary:
        'One column and a sentence. An empty second column would say the other side has a blank ' +
        'version of it, which is a different and wronger fact.',
      placement: {
        component: 'caos-difference-modal',
        inputs: {
          item: 'LegacyImporter',
          'source-name': 'UAT',
          'target-name': 'Production',
          missing: 'target',
          sourceText: 'public class LegacyImporter {\n  // retired, but still in UAT\n}',
        },
      },
      stage: { needsWidth: 860 },
    },
    {
      key: 'identical',
      label: 'Two versions that match',
      summary:
        'Said, rather than drawn as two columns with nothing highlighted — which leaves somebody ' +
        'hunting for a change that is not there.',
      placement: {
        component: 'caos-difference-modal',
        inputs: {
          item: 'QuoteCalculator',
          'source-name': 'UAT',
          'target-name': 'Production',
          sourceText: 'public class QuoteCalculator {\n  // unchanged\n}',
          targetText: 'public class QuoteCalculator {\n  // unchanged\n}',
        },
      },
      stage: { needsWidth: 860 },
    },
  ],
};

export default docs;
